import React, { useCallback } from 'react';
import { Paper, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { useModePanelStyle, useModeFormStyle, BaseInfoCompProps, BaseEntityField, InfoCompPanelHeader, ModeInfoCompTextInputField,
    EntityFieldsCollection, ModeFormInputSection } from '@moderepo/mode-app-components';
import { AlertConditionType, AlertSeverity, AlertState } from '@moderepo/mode-apis';



const useStyle = makeStyles((theme: Theme) => {
    return {

        statusInputField: {
            '& input': {
                fontWeight: 'bold',
            },

            '&.active': {
                '& input': {
                    color: theme.palette.success.main,
                },
            },

            '&.inactive': {
                '& input': {
                    color: theme.palette.error.main,
                },
            },
        },

        stateInputField: {
            '& input': {
                fontWeight: 'bold',
            },

            [`&.state-${AlertState.ALERTING}`]: {
                '& input': {
                    color: theme.palette.error.main,
                },
            },

            [`&.state-${AlertState.INEFFECTIVE}`]: {
                '& input': {
                    color: theme.palette.info.main,
                },
            },

            [`&.state-${AlertState.OBSOLETE}`]: {
                '& input': {
                    color: theme.palette.info.main,
                },
            },

            [`&.state-${AlertState.RECOVERED}`]: {
                '& input': {
                    color: theme.palette.success.main,
                },
            },

        },

        severityInputField: {
            '& input': {
                fontWeight: 'bold',
            },

            [`&.severity-${AlertSeverity.LOW}`]: {
                '& input': {
                    color: theme.palette.info.main,
                },
            },

            [`&.severity-${AlertSeverity.MODERATE}`]: {
                '& input': {
                    color: theme.palette.warning.main,
                },
            },

            [`&.severity-${AlertSeverity.HIGH}`]: {
                '& input': {
                    color: theme.palette.error.main,
                },
            },
        },

        problemInputField: {
            '& input': {
                color: theme.palette.error.main,
            },
        },
    };
}, { name: 'AlertInfo', index: 1 });


/**
 * The fields required for displaying Target Metrics
 */
interface TargetMetricFields extends EntityFieldsCollection {
    readonly entityClass: BaseEntityField<string>;
    readonly metricsDefinitionId: BaseEntityField<string>;
    readonly metricName?: BaseEntityField<string> | undefined;
    readonly targetBy?: BaseEntityField<string> | undefined;
    readonly entityIds?: BaseEntityField<string> | undefined;
    readonly exclude?: BaseEntityField<boolean> | undefined;
}


/**
 * The fields required for displaying metrics condition
 */
interface MetricsConditionFields extends EntityFieldsCollection {
    readonly targetMetric: TargetMetricFields;
}


/**
 * The fields required for displaying Heartbeat condition
 */
interface MetricsHeartbeatConditionFields extends MetricsConditionFields {
    readonly severity: BaseEntityField<AlertSeverity>;
}


/**
 * The fields required for displaying Threshold
 */
interface ThresholdFields extends EntityFieldsCollection {
    readonly severity: BaseEntityField<AlertSeverity>;
    readonly operator: BaseEntityField<string>;
    readonly operands: BaseEntityField<string>;
}


/**
 * The fields required for displaying Threshold condition
 */
interface MetricsThresholdConditionFields extends MetricsConditionFields {
    readonly aggregation: BaseEntityField<string>;
    readonly thresholds: readonly ThresholdFields[]
}


/**
 * Type guard function to check if the provided obj is a HeartbeatConditionFields object
 */
const isHeartbeatConditionFields = (obj: unknown): obj is MetricsHeartbeatConditionFields => {
    const fields = obj as MetricsHeartbeatConditionFields;
    return fields.severity !== undefined && fields.targetMetric !== undefined;
};



/**
 * Type guard function to check if the provided obj is a ThresholdConditionFields object
 */
const isThresholdConditionFields = (obj: unknown): obj is MetricsThresholdConditionFields => {
    const fields = obj as MetricsThresholdConditionFields;
    return fields.aggregation !== undefined && fields.targetMetric !== undefined && fields.thresholds !== undefined;
};


/**
 * The props required for AlertInfo component
 */
export interface AlertInfoProps extends BaseInfoCompProps {
    readonly fields: {
        readonly id: BaseEntityField<string>;
        readonly description: BaseEntityField<string>;
        readonly type: BaseEntityField<AlertConditionType>;
        readonly severity: BaseEntityField<AlertSeverity>;
        readonly state: BaseEntityField<AlertState>;
        readonly entityId: BaseEntityField<string>;
        readonly recoveryTime: BaseEntityField<string>;
        readonly invocationTime: BaseEntityField<string>;
        readonly alertRule: {
            readonly condition: MetricsHeartbeatConditionFields | MetricsThresholdConditionFields | undefined;
        }
    }

    readonly texts: {
        readonly alertRuleTitle: string;
        readonly alertConditionsTitle: string;
        readonly metricTargetTitle: string;
        readonly metricTargetsTitle: string;
    }
}


/**
 * This is a DUMB component used for displaying Alert info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const AlertInfo = (props: AlertInfoProps) => {

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();



    /**
     * Create the fields for the metricTarget settings
     */
    const getTargetFields = useCallback((targetMetricFields: TargetMetricFields) => {
        return (
            <ModeFormInputSection
                key={`${targetMetricFields.entityClass.displayValue}
                -${targetMetricFields.metricsDefinitionId.displayValue}
                -${targetMetricFields.metricName?.displayValue}`}
                collapsible={false}
                title={props.texts.metricTargetTitle}
            >
                <div className={formClasses.formInputSectionContent}>
                    <div className={formClasses.formInputGroup}>
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={targetMetricFields.entityClass}
                            fieldName="entityClass"
                            inputValue={targetMetricFields.entityClass.displayValue ?? targetMetricFields.entityClass.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={targetMetricFields.metricsDefinitionId}
                            fieldName="metricsDefinitionId"
                            inputValue={targetMetricFields.metricsDefinitionId.displayValue ?? targetMetricFields.metricsDefinitionId.value}
                        />
                        {targetMetricFields.metricName && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={targetMetricFields.metricName}
                                fieldName="metricName"
                                inputValue={targetMetricFields.metricName.displayValue ?? targetMetricFields.metricName.value}
                            />
                        )}
                    </div>
                    {targetMetricFields.targetBy && targetMetricFields.entityIds && targetMetricFields.exclude && (
                        <div className={formClasses.formInputGroup}>
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={targetMetricFields.targetBy}
                                fieldName="targetBy"
                                inputValue={targetMetricFields.targetBy.displayValue ?? targetMetricFields.targetBy.value}
                            />
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={targetMetricFields.entityIds}
                                fieldName="entityIds"
                                inputValue={targetMetricFields.entityIds.displayValue ?? targetMetricFields.entityIds.value}
                            />
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={targetMetricFields.exclude}
                                fieldName="exclude"
                                inputValue={targetMetricFields.exclude.displayValue ?? targetMetricFields.exclude.value}
                            />
                        </div>
                    )}
                </div>
            </ModeFormInputSection>
        );
    }, [formClasses.formInputField, formClasses.formInputGroup, formClasses.formInputSectionContent, props]);


    /**
     * Create the fields for the threshold condition settings
     */
    const getConditionThresholdFields = useCallback((thresholdFields: ThresholdFields) => {
        return (
            <ModeFormInputSection
                key={`${thresholdFields.severity.displayValue}
                -${thresholdFields.operator.displayValue}
                -${thresholdFields.operands.displayValue}`}
                collapsible={false}
                title={props.texts.alertConditionsTitle}
            >
                <div className={formClasses.formInputSectionContent}>
                    <div className={formClasses.formInputGroup}>
                        <ModeInfoCompTextInputField
                            className={clsx(
                                formClasses.formInputField, classes.severityInputField,
                                `severity-${thresholdFields.severity.value}`,
                            )}
                            inputType="text"
                            compProps={props}
                            inputProps={thresholdFields.severity}
                            fieldName="severity"
                            inputValue={thresholdFields.severity.displayValue ?? thresholdFields.severity.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={thresholdFields.operator}
                            fieldName="operator"
                            inputValue={thresholdFields.operator.displayValue ?? thresholdFields.operator.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={thresholdFields.operands}
                            fieldName="operands"
                            inputValue={thresholdFields.operands.displayValue ?? thresholdFields.operands.value}
                        />
                    </div>
                </div>
            </ModeFormInputSection>
        );
    }, [classes.severityInputField, formClasses.formInputField, formClasses.formInputGroup, formClasses.formInputSectionContent, props]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form className={clsx(formClasses.root, props.formProps?.className)} noValidate autoComplete="on" method="POST">

                    {/* Basic Alert attributes */}
                    <div className={formClasses.formInputGroup}>
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.id}
                            fieldName="id"
                            inputValue={props.fields.id.displayValue ?? props.fields.id.value}
                        />
                        <ModeInfoCompTextInputField
                            className={clsx(
                                formClasses.formInputField, classes.severityInputField,
                                `severity-${props.fields.severity.value}`,
                            )}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.severity}
                            fieldName="severity"
                            inputValue={props.fields.severity.displayValue ?? props.fields.severity.value}
                        />
                        <ModeInfoCompTextInputField
                            className={clsx(
                                formClasses.formInputField, classes.stateInputField,
                                `state-${props.fields.state.value}`,
                            )}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.state}
                            fieldName="state"
                            inputValue={props.fields.state.displayValue ?? props.fields.state.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            multiline
                            compProps={props}
                            inputProps={props.fields.description}
                            fieldName="description"
                            inputValue={props.fields.description.displayValue ?? props.fields.description.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.type}
                            fieldName="type"
                            inputValue={props.fields.type.displayValue ?? props.fields.type.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.entityId}
                            fieldName="entityId"
                            inputValue={props.fields.entityId.displayValue ?? props.fields.entityId.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.invocationTime}
                            fieldName="invocationTime"
                            inputValue={props.fields.invocationTime.displayValue ?? props.fields.invocationTime.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.recoveryTime}
                            fieldName="recoveryTime"
                            inputValue={props.fields.recoveryTime.displayValue ?? props.fields.recoveryTime.value}
                        />
                    </div>

                    {/* Alert rule condition attributes */}
                    <ModeFormInputSection
                        collapsible={false}
                        title={props.texts.alertRuleTitle}
                        icon="notifications"
                    >
                        {isThresholdConditionFields(props.fields.alertRule.condition) && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.alertRule.condition.aggregation}
                                fieldName="aggregation"
                                inputValue={props.fields.alertRule.condition.aggregation.displayValue
                                    ?? props.fields.alertRule.condition.aggregation.value}
                            />
                        )}

                        {(isHeartbeatConditionFields(props.fields.alertRule.condition)
                            || isThresholdConditionFields(props.fields.alertRule.condition)) && (
                            getTargetFields(props.fields.alertRule.condition.targetMetric)
                        )}

                        {isThresholdConditionFields(props.fields.alertRule.condition) && (
                            props.fields.alertRule.condition.thresholds.map((thresholdFields) => {
                                return getConditionThresholdFields(thresholdFields);
                            })
                        )}
                    </ModeFormInputSection>
                </form>
            </div>

        </Paper>
    );

};
